'use client'

import { useRef } from 'react'

import { reachInk, useRowGeometry } from '@/features/landing/method/reach'
import type { Beat } from '@/features/landing/walk/walk'

const BEATS: Beat[] = [1, 2, 3, 4, 5, 6]

// The caption's index (spec §4): the six beats as a running index on one
// hairline, in the Method stepper's idiom (§3.1) — numerals, tabular,
// muted except the beats reached; one ink underline under the current
// beat, stretching toward the next as the beat's scrub progresses and
// springing across when the beat turns. Visual only: the caption's live
// region announces the beat in words. The row's geometry is read once
// (and on resize), not on every scrubbed frame; the ink is arithmetic.
export function BeatIndex({ beat, t }: { beat: Beat; t: number }) {
    const ref = useRef<HTMLDivElement>(null)
    const row = useRowGeometry(ref)
    // null until measured, so the server HTML paints no full-width line
    const ink = row ? reachInk(row.items, beat - 1, t, row.width) : null
    return (
        <div
            ref={ref}
            className="relative border-b border-rule pb-1.5"
            aria-hidden
        >
            <ol className="flex gap-4 font-serif text-lp-caption tracking-lp-16 tabular-nums">
                {BEATS.map((b) => (
                    <li
                        key={b}
                        data-reached={b <= beat ? '' : undefined}
                        className={
                            b <= beat
                                ? 'text-ink transition-colors duration-300'
                                : 'text-muted-fg transition-colors duration-300'
                        }
                    >
                        {String(b).padStart(2, '0')}
                    </li>
                ))}
            </ol>
            {ink ? (
                <span
                    className="absolute -bottom-px h-0.5 bg-ink transition-[left] duration-[600ms] ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none"
                    style={{ left: ink.left, right: ink.right }}
                />
            ) : null}
        </div>
    )
}
